import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import "./OTPInputBox.css";

function OTPInputBox(props) {
  const [otpValue, setOtpValue] = useState(Array(6).fill(""));
  const inputRefs = useRef([
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null),
    useRef(null)
  ]);

  useEffect(() => {
    const newValue = otpValue.join("");
    if (typeof props.setValue === "function") {
      props.setValue(newValue);
    }
    // eslint-disable-next-line
  }, [otpValue]);

  const updateOtp = (key, refIndex) => {
    if (key === "") {
      const newOTPValue = [...otpValue];
      newOTPValue[refIndex] = "";
      setOtpValue(newOTPValue);

      if (refIndex > 0) {
        inputRefs.current[refIndex - 1]?.focus();
      }
      return;
    }

    if (/^\d+$/.test(key)) {
      const newOTPValue = [...otpValue];

      if (key.length === 1) {
        newOTPValue[refIndex] = key;
        setOtpValue(newOTPValue);

        if (refIndex < 5) {
          setTimeout(() => {
            inputRefs.current[refIndex + 1]?.focus();
          }, 0);
        }
      } else {
        for (const [index, number] of key.split("").entries()) {
          if (refIndex + index > 5) break;
          newOTPValue[refIndex + index] = number;
        }
        setOtpValue(newOTPValue);

        const nextFocus = Math.min(refIndex + key.length, 5);
        setTimeout(() => {
          inputRefs.current[nextFocus]?.focus();
        }, 0);
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace") {
      if (!otpValue[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      } else {
        const newOTPValue = [...otpValue];
        newOTPValue[index] = "";
        setOtpValue(newOTPValue);
      }
    }

    if (e.key === "Delete") {
      const newOTPValue = [...otpValue];
      newOTPValue[index] = "";
      setOtpValue(newOTPValue);

      if (index < 5) {
        inputRefs.current[index + 1]?.focus();
      }
    }

    if (e.key === "ArrowRight" && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (e.key === "ArrowLeft" && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData)
      .getData("text")
      .trim();

    if (!/^\d+$/.test(text)) {
      return;
    }
    const digits = text.slice(0, 6).split("");
    const newOTPValue = [...otpValue];

    for (let i = 0; i < digits.length && i < 6; i++) {
      newOTPValue[i] = digits[i];
    }

    setOtpValue(newOTPValue);
    props.setValue?.(newOTPValue.join(""));

    if (digits.length < 6) {
      setTimeout(() => {
        inputRefs.current[digits.length]?.focus();
      }, 0);
    } else {
      setTimeout(() => {
        inputRefs.current[5]?.focus();
      }, 0);
    }
  };

  return (
    <>
      {!props.removeDefaultLabel && (
        <p
          className={`${props.labelClassName} text-align-center font-size-smaller letter-spacing-large`}
        >
          {props.label || "PLEASE ENTER OTP"}
        </p>
      )}
      <div
        className={`flex-justify-content-space-between inherit-parent-width margin-top-small ${
          props.className || ""
        }`}
        data-cy={"OTP-box"}
      >
        {otpValue.map((val, idx) => (
          <input
            key={idx}
            ref={(el) => (inputRefs.current[idx] = el)}
            autoFocus={idx === 0}
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            value={val}
            onChange={(e) => updateOtp(e.target.value.replace(/\D/g, ""), idx)}
            onKeyDown={(e) => handleKeyDown(e, idx)}
            onPaste={idx === 0 ? handlePaste : undefined}
            className={`${
              props.size === "small"
                ? "otp-input-box-size-small"
                : "otp-input-box-size"
            } max-width-54px padding-default ${
              props.error
                ? "border-bottom-otp-box-error"
                : "border-bottom-otp-box"
            } background-white font-size-medium flex-center-children-horizontally text-align-center font-family-gilroy-regular font-color-secondary`}
            autoComplete="one-time-code"
            data-cy={`otp${idx + 1}`}
          />
        ))}
      </div>
    </>
  );
}

OTPInputBox.propTypes = {
  /**
   * function which give OTP value as arguments
   */
  setValue: PropTypes.func,
  /**
   * which is used to style to style the parent element
   */
  className: PropTypes.string,
  /**
   * label text property
   */
  label: PropTypes.string,
  /**
   * error state
   */
  error: PropTypes.bool,
  /**
   * size variant
   */
  size: PropTypes.string,
  /**
   * current OTP value
   */
  value: PropTypes.string,
  /**
   * remove default label
   */
  removeDefaultLabel: PropTypes.bool
};

OTPInputBox.defaultProps = {
  setValue: undefined,
  className: "",
  label: "PLEASE ENTER OTP",
  error: false,
  size: "normal",
  value: "",
  removeDefaultLabel: false
};

export default OTPInputBox;
